import { FeatureTypes } from "entity-app/constants";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import ResizableSplitLayout from "components/resizable-split-layout";
import { useCIQLazyQuery, useCIQQuery } from "hooks/ciq-gql-hooks";
import { GridLoadingIndicator } from "components/widgets";
import { ProjectContext } from "context/ProjectProvider";
import HeaderTitle from "./components/header-title-ui";
import { getFeatureDetail, hasIntersection } from "./utils";
import {
  ENTITIES_DROPDOWN_NAMES,
  ENTITIES_LINKING_INFO
} from "./queries/queries";
import {
  DropDownDBFeatureInstance,
  TEditingEntity,
  TEntityLink,
  TLinkSource
} from "./models";
import LeftSideLinkData from "./components/left-side-link-data";
import LinkingInstructions from "./components/instructions";
import ListingLinkingWindow from "./components/listing-components";
import useActivityListHook from "./hooks/activity-list-hook";
import { initialTranslateEntityData } from "./utils/translate-to-linking-object";
import { isAiRecommendationTabVisible } from "./utils/ai-link-feedback";

import "./index.scss";

const getNewlyLinkedIds = (
  updatedlist: TLinkSource[],
  prevList: TLinkSource[]
): string[] =>
  updatedlist
    .filter(
      (item) =>
        !prevList.some((x) => x.featureInstanceId === item.featureInstanceId)
    )
    .map((item) => item.featureInstanceId);

const getNestedActivityIds = (
  updatedlist: TLinkSource[],
  prevList: TLinkSource[]
): { activityId: string; parentMaterialId: string }[] => {
  const nestedActivityIds: {
    activityId: string;
    parentMaterialId: string;
  }[] = [];

  updatedlist.forEach((item) => {
    const existingItem = prevList.find(
      (x) => x.featureInstanceId === item.featureInstanceId
    );
    if (!existingItem?.feature_instance?.links) {
      return;
    }

    const existingActivityIds =
      existingItem.feature_instance.links.map((a) => a.featureInstanceId) || [];

    item.feature_instance?.links?.forEach((activity) => {
      if (!existingActivityIds.includes(activity.featureInstanceId)) {
        nestedActivityIds.push({
          activityId: activity.featureInstanceId,
          parentMaterialId: item.featureInstanceId
        });
      }
    });
  });

  return nestedActivityIds;
};

const shouldKeepUnlinkedItem = (
  unlinked: {
    parentMaterialId?: string;
    item: { featureInstanceId: string };
  },
  newlyLinkedIds: string[],
  nestedActivityIds: { activityId: string; parentMaterialId: string }[]
): boolean => {
  if (
    !unlinked.parentMaterialId &&
    newlyLinkedIds.includes(unlinked.item.featureInstanceId)
  ) {
    return false;
  }

  if (
    unlinked.parentMaterialId &&
    nestedActivityIds.some(
      (nested) =>
        nested.activityId === unlinked.item.featureInstanceId &&
        nested.parentMaterialId === unlinked.parentMaterialId
    )
  ) {
    return false;
  }

  return true;
};

type Props = {
  featureTypeId: FeatureTypes;
  leftTabEntityTypes: FeatureTypes[];
  state?: any;
};

function FeatureLinking(props: Props) {
  const { featureTypeId, leftTabEntityTypes, state } = props;
  const { gqlClientForProject, eventLogs, isFeatureFlagEnabled } =
    useContext(ProjectContext);
  const [loading, setLoading] = useState<boolean>(false);

  const [selectedEntityIds, setSelectedEntityIds] = useState<Array<string>>(
    () => {
      return state?.selectedRows?.map((x: any) => x.id) || [];
    }
  );

  const [dependentIdsForEventLog, setDependentIdsForEventLog] = useState<
    string[]
  >([]);

  const [selectedEntityInfo, setSelectedEntityInfo] = useState<TEntityLink>();

  const [editingEntity, setEditingEntity] = useState<TEditingEntity | null>(
    null
  );

  const onUpdateEditingEntityList = ({
    updatedlist
  }: {
    updatedlist: TLinkSource[];
  }) => {
    setEditingEntity((prev) => {
      if (!prev) return prev;

      const newlyLinkedIds = getNewlyLinkedIds(updatedlist, prev.list);
      const nestedActivityIds = getNestedActivityIds(updatedlist, prev.list);
      const updatedUnlinkedItems = prev.unlinkedItems.filter((unlinked) =>
        shouldKeepUnlinkedItem(unlinked, newlyLinkedIds, nestedActivityIds)
      );

      return {
        ...prev,
        list: updatedlist,
        unlinkedItems: updatedUnlinkedItems
      };
    });
  };

  const activityListHook = useActivityListHook({
    preferredScheduleId: state?.selectedScheduleId,
    uploadedScheduleName: state?.uploadedScheduleName,
    uploadedScheduleDate: state?.uploadedScheduleDate,
    selectedEntityIds
  });

  const aiRecommendationTabVisible = isAiRecommendationTabVisible({
    editingEntity,
    selectedEntityInfo,
    smartLinkingEnabled: Boolean(isFeatureFlagEnabled?.("SMART_LINKING")),
    hasCompletedRecommenderJob: Boolean(
      activityListHook.hasCompletedRecommenderJob
    )
  });

  const { data: featureSubmittalList, refetch: refetchSubmittalList } =
    useCIQQuery<{
      feature_instance: Array<DropDownDBFeatureInstance>;
    }>(ENTITIES_DROPDOWN_NAMES, {
      variables: {
        feature_type_id: FeatureTypes.SUBMITTALS,
        excluded_instance_id: []
      },
      client: gqlClientForProject,
      skip: !gqlClientForProject
    });

  const { data: featureMaterialList, refetch: refetchMaterialList } =
    useCIQQuery<{
      feature_instance: Array<DropDownDBFeatureInstance>;
    }>(ENTITIES_DROPDOWN_NAMES, {
      variables: {
        feature_type_id: FeatureTypes.MATERIALS,
        excluded_instance_id:
          selectedEntityIds.length > 0 ? selectedEntityIds : []
      },
      client: gqlClientForProject,
      skip: !gqlClientForProject
    });

  const [getEntityLinkingInfoQuery] = useCIQLazyQuery(ENTITIES_LINKING_INFO);

  const featureDetail = useMemo(
    () => getFeatureDetail(featureTypeId),
    [featureTypeId]
  );

  const getEntityInfo = useCallback(async () => {
    const response = await getEntityLinkingInfoQuery({
      feature_instance_ids: selectedEntityIds
    });

    if (response.data && response.data?.data?.feature_instance?.length) {
      setSelectedEntityInfo(
        initialTranslateEntityData(response.data.data.feature_instance)
      );
    }
  }, [getEntityLinkingInfoQuery, selectedEntityIds]);

  const { refetchActivityList } = activityListHook;
  const refreshLinkedEntityLists = useCallback(() => {
    return Promise.all([
      refetchMaterialList(),
      refetchSubmittalList(),
      refetchActivityList?.()
    ]);
  }, [refetchActivityList, refetchMaterialList, refetchSubmittalList]);

  const refetchLinkingScreen = useCallback(async () => {
    await Promise.all([getEntityInfo(), refreshLinkedEntityLists()]);
  }, [getEntityInfo, refreshLinkedEntityLists]);

  const wasEditingRef = useRef(false);
  useEffect(() => {
    if (editingEntity) {
      wasEditingRef.current = true;
      return;
    }
    if (!wasEditingRef.current) return;
    wasEditingRef.current = false;
    refreshLinkedEntityLists();
  }, [editingEntity, refreshLinkedEntityLists]);

  useEffect(() => {
    if (selectedEntityInfo) {
      const dependentIds = [
        ...selectedEntityInfo.ids,
        ...selectedEntityInfo.links.map((x) => x.featureInstanceId)
      ];

      setDependentIdsForEventLog(dependentIds);
    } else {
      setDependentIdsForEventLog([]);
    }
  }, [selectedEntityInfo]);

  const previousEventLogs = useRef(eventLogs);
  useEffect(() => {
    if (eventLogs.length && previousEventLogs.current !== eventLogs) {
      if (
        eventLogs.some((log) => log.data_source === "schedule_import_details")
      ) {
        activityListHook.refetchScheduleVersions();
      }

      if (!editingEntity) {
        const newEventLogs = eventLogs.filter((x) =>
          hasIntersection(
            x.info?.feature_instance_ids || [],
            dependentIdsForEventLog
          )
        );
        if (newEventLogs.length) {
          getEntityInfo();
        }
      }
    }
    previousEventLogs.current = eventLogs;
  }, [
    eventLogs,
    getEntityInfo,
    editingEntity,
    dependentIdsForEventLog,
    activityListHook
  ]);

  const getCallEntityInfoWithLoading = useCallback(async () => {
    setLoading(true);
    await getEntityInfo();
    setLoading(false);
  }, [getEntityInfo]);

  const prevIds = useRef<string[]>([]);
  useEffect(() => {
    if (
      selectedEntityIds.length > 0 &&
      !hasIntersection(prevIds.current, selectedEntityIds)
    ) {
      getCallEntityInfoWithLoading();
    }
    prevIds.current = selectedEntityIds;
  }, [getCallEntityInfoWithLoading, selectedEntityIds]);

  return (
    <div className="flex h-full w-full flex-col overflow-hidden feature-entity-linking-page">
      <HeaderTitle
        featureDetail={featureDetail}
        selectedEntityIds={selectedEntityIds}
        setSelectedEntityIds={setSelectedEntityIds}
        selectedEntityInfo={selectedEntityInfo}
        setSelectedEntityInfo={setSelectedEntityInfo}
        refetchEntityInfo={getCallEntityInfoWithLoading}
        disable={!!editingEntity}
      />
      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
        {loading && (
          <div
            data-testid="feature-linking-loading"
            className={
              selectedEntityInfo
                ? "absolute inset-0 z-10 flex items-center justify-center bg-white bg-opacity-60"
                : "flex h-full w-full flex-col items-center justify-center"
            }
          >
            <GridLoadingIndicator />
          </div>
        )}
        {selectedEntityInfo && (
          <ResizableSplitLayout
            defaultLeftPercent={40}
            minLeftPercent={20}
            maxLeftPercent={60}
            left={
              <LeftSideLinkData
                selectedEntityInfo={selectedEntityInfo}
                leftTabEntityTypes={leftTabEntityTypes}
                editingEntity={editingEntity}
                setEditingEntity={setEditingEntity}
                refetchEntityInfo={refetchLinkingScreen}
                featureTypeId={featureTypeId}
                isAiRecommendationTabVisible={aiRecommendationTabVisible}
              />
            }
            right={
              editingEntity ? (
                <ListingLinkingWindow
                  editingEntity={editingEntity}
                  onUpdateEditingEntityList={onUpdateEditingEntityList}
                  setEditingEntity={setEditingEntity}
                  materials={featureMaterialList?.feature_instance || []}
                  submittals={featureSubmittalList?.feature_instance || []}
                  activityListHook={activityListHook}
                  selectedEntityInfo={selectedEntityInfo}
                />
              ) : (
                <div className="linking-group--right h-full min-h-0 overflow-hidden">
                  <div className="flex h-10 items-center p-2.5">
                    <div className="font-bold">Instructions</div>
                  </div>
                  <LinkingInstructions
                    featureTypeId={featureTypeId}
                    isBulk={selectedEntityIds.length > 1}
                  />
                </div>
              )
            }
          />
        )}
      </div>
    </div>
  );
}

export default FeatureLinking;
